// 应用侧栏：shadcn sidebar-07（可收起为图标），variant="inset"。
import { useQuery } from "@tanstack/react-query";
import { Activity, Archive, ArrowLeft, BookMarked, ChartColumn, FolderOpen, Gauge, LayoutTemplate, ListChecks, Package, Palette, ScrollText, Server, Settings2, Shield, ShieldAlert, Swords, UserCog, Users } from "lucide-react";
import { epb, myTurn, qkb } from "@/lib/api/bookings";
import type { ComponentProps } from "react";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Sidebar, SidebarContent, SidebarFooter, SidebarHeader, SidebarRail } from "@/components/ui/sidebar";
import { ep, qk } from "@/lib/api/endpoints";
import { useMe } from "@/lib/auth/session";
import { useSpace } from "@/lib/space";
import { NavMain, type NavItem } from "./nav-main";
import { NavRecent } from "./nav-recent";
import { NavUser } from "./nav-user";
import { SpaceSwitcher } from "./space-switcher";

/** 当前空间解析中 / 排队的 demo 数量。 */
function useProcessingCount() {
  const { space } = useSpace();
  const f = space.kind === "team" ? { team_id: space.team.id } : { owner_type: "user" as const };
  const parsing = useQuery({ queryKey: qk.demos({ ...f, status: "parsing", limit: 20 }), queryFn: () => ep.demos({ ...f, status: "parsing", limit: 20 }), refetchInterval: 15_000 });
  const queued = useQuery({ queryKey: qk.demos({ ...f, status: "queued", limit: 20 }), queryFn: () => ep.demos({ ...f, status: "queued", limit: 20 }), refetchInterval: 15_000 });
  return (parsing.data?.items.length ?? 0) + (queued.data?.items.length ?? 0);
}

/** 当前战队待处理的挑战 / 选边确认数。 */
function useChallengeCount() {
  const { space } = useSpace();
  const teamId = space.kind === "team" ? space.team.id : "";
  const q = useQuery({ queryKey: qkb.upcoming(teamId), queryFn: () => epb.upcoming(teamId), enabled: !!teamId, refetchInterval: 60_000 });
  return (q.data?.items ?? []).filter(myTurn).length;
}

export function AppSidebar(props: ComponentProps<typeof Sidebar>) {
  const me = useMe();
  const { space } = useSpace();
  const processing = useProcessingCount();
  const challenges = useChallengeCount();

  const work: NavItem[] = [
    { title: "Demo 库", url: "/demos", icon: FolderOpen, badge: processing > 0 ? <Badge tone="warning">{processing}</Badge> : undefined },
    { title: "对战", url: "/matches", icon: Swords, badge: challenges > 0 ? <Badge tone="warning">{challenges}</Badge> : undefined },
    { title: "战术库", url: "/tactics", icon: BookMarked },
    { title: "战队", url: space.kind === "team" ? `/teams/${space.team.id}` : "/teams", icon: Users },
    { title: "皮肤", url: "/skins", icon: Palette },
    {
      title: "设置",
      url: "/settings",
      icon: Settings2,
      items: [
        { title: "个人资料", url: "/settings", end: true },
        { title: "密码与安全", url: "/settings/security" },
        { title: "通知", url: "/settings/notifications" },
      ],
    },
  ];

  const analysis: NavItem[] = [
    {
      title: "深度分析",
      url: "/analysis",
      icon: ChartColumn,
      items: [{ title: "历史数据分析", url: "/analysis/history" }],
    },
  ];

  return (
    <Sidebar collapsible="icon" variant="inset" {...props}>
      <SidebarHeader>
        <SpaceSwitcher />
      </SidebarHeader>
      <SidebarContent>
        <NavMain label="工作区" items={work} />
        <NavMain label="分析" items={analysis} />
        <NavRecent />
        {/* 管理后台入口固定在导航区底部、用户组件上方，不带分组标签 */}
        {me.user.role === "admin" ? <NavMain className="mt-auto" items={[{ title: "管理后台", url: "/admin", icon: Shield }]} /> : null}
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}

export function AdminSidebar(props: ComponentProps<typeof Sidebar>) {
  const run: NavItem[] = [
    { title: "总览", url: "/admin", icon: Gauge, end: true },
    { title: "Worker 与任务", url: "/admin/workers", icon: Activity },
  ];
  const ops: NavItem[] = [
    { title: "用户", url: "/admin/users", icon: Users },
    { title: "Demo", url: "/admin/demos", icon: FolderOpen },
    { title: "敏感词", url: "/admin/sensitive-words", icon: ShieldAlert },
  ];
  const match: NavItem[] = [
    { title: "对战服务器", url: "/admin/servers", icon: Server },
    { title: "比赛", url: "/admin/matches", icon: Swords },
    { title: "服务器 Demo", url: "/admin/server-demos", icon: Archive },
  ];
  const serverOps: NavItem[] = [
    { title: "运维任务", url: "/admin/server-tasks", icon: ListChecks },
    { title: "配置模板", url: "/admin/server-templates", icon: LayoutTemplate },
    { title: "游戏内管理员", url: "/admin/server-admins", icon: UserCog },
    { title: "插件版本", url: "/admin/server-components", icon: Package },
  ];
  return (
    <Sidebar collapsible="icon" variant="inset" {...props}>
      <SidebarHeader>
        <Link to="/admin" className="admin-brand link-plain px-1 pt-1 pb-1 group-data-[collapsible=icon]:px-0">
          <span className="admin-tile">
            <Shield className="ico" strokeWidth={2} aria-hidden="true" />
          </span>
          <span className="flex flex-col group-data-[collapsible=icon]:hidden">
            <span className="body-medium">管理后台</span>
            <span className="caption muted">cs2coach</span>
          </span>
        </Link>
      </SidebarHeader>
      <SidebarContent>
        <NavMain label="运行" items={run} />
        <NavMain label="对战" items={match} />
        <NavMain label="服务器运维" items={serverOps} />
        <NavMain label="运营" items={ops} />
        <NavMain label="支持" items={[{ title: "操作日志", url: "/admin/audit", icon: ScrollText }]} />
        <div className="flex-auto" />
        <NavMain label="应用" items={[{ title: "返回应用", url: "/demos", icon: ArrowLeft, end: true }]} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser admin />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
